<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { ProjectUpdateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  projectId: number;
  projectName: string;
  projectDescription: string;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const projectName = ref<string>(props.projectName);
const projectDescription = ref<string>(props.projectDescription);

const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ProjectUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.projectUpdate) {
    console.debug('Project updated:', res.data.projectUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Project update failed: ' + res.data;
    console.error('Project update failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error updating project:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onProjectUpdateConfirmed() {
  if (!projectName.value) {
    mutationError.value = 'You must provide project name';
    console.error('Project name not provided');
    return;
  }
  console.debug('Updating project:', projectName.value);

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.projectId,
    name: projectName.value,
    description: projectDescription.value,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Modal Title -->
      <template #title>
        <font-awesome-icon icon="pencil" class="me-2" />
        Update Project Details
      </template>
      <!-- Modal Content -->
      <div>
        <!-- Project name -->
        <BFormGroup
          label="Project name"
          label-for="new-project-name"
          class="mt-3"
        >
          <BFormInput
            v-model="projectName"
            id="new-project-name"
            name="new-project-name"
            placeholder="Enter a name for the project"
            required
          ></BFormInput>
        </BFormGroup>
        <!-- Sample description -->
        <BFormGroup
          label="Sample description"
          label-for="new-sample-description"
          class="mt-3"
        >
          <BFormTextarea
            v-model="projectDescription"
            id="new-project-description"
            name="new-project-description"
            placeholder="A brief description of the project."
          ></BFormTextarea>
        </BFormGroup>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="onProjectUpdateConfirmed"
          :disabled="mutationInProgress || mutationShowSuccess"
        >
          <span v-if="mutationInProgress">
            Updating Project
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Project Updated
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Update Project </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
